import { createReducer } from 'typesafe-actions'
import {
  GetBusinessEntityDetailsResponseDto,
  ReadBusinessEntitiesRequestDto,
  ReadBusinessEntitiesResponseDto,
  GetUsersForAssignEntityResponseDto,
  BusinessEntityUserDto,
  ReadBusinessEntitiesForAssignToUserResponseDto,
  CreateBusinessEntityResponseDto,
} from '@sparling/financial-common'
import { omit } from 'lodash'
import { ActionTypes } from '../types'
import { LoadingStateEnum } from '../../types'
import {
  getBusinessEntitiesRequest,
  getBusinessEntitiesSuccess,
  getBusinessEntitiesFailure,
  resetBusinessEntitiesFilters,
  getBusinessEntityDetailsRequest,
  getBusinessEntityDetailsSuccess,
  getBusinessEntityDetailsFailure,
  getUsersForAssignEntityRequest,
  getUsersForAssignEntitySuccess,
  getUsersForAssignEntityFailure,
  updateBusinessEntityDetailsSuccess,
  updateBusinessEntityUsersSuccess,
  updateBusinessEntitySettingsSuccess,
  deleteBusinessEntityUsersSuccess,
  getBusinessEntitiesForAssignToUserRequest,
  getBusinessEntitiesForAssignToUserSuccess,
  getBusinessEntitiesForAssignToUserFailure,
  createBusinessEntitiesRequest,
  createBusinessEntitiesSuccess,
  createBusinessEntitiesFailure,
  resetCreatedBusinessEntity,
} from '../actions'

export type BusinessEntitiesState = {
  loading: LoadingStateEnum
  data: ReadBusinessEntitiesResponseDto
  filters: ReadBusinessEntitiesRequestDto
  error: Error | null
  createBusinessEntity: CreateBusinessEntity
  currentBusinessEntity: CurrentBusinessEntity
  usersForAssignEntity: UsersForAssignEntity
  entitiesForAssignToUser: EntitiesForAssignToUser
}

type CreateBusinessEntity = {
  loading: LoadingStateEnum
  data: CreateBusinessEntityResponseDto | null
  error: Error | null
}

type CurrentBusinessEntity = {
  id: string | null
  loading: LoadingStateEnum
  data:
    | (Omit<GetBusinessEntityDetailsResponseDto, 'users'> & {
        users: {
          items: BusinessEntityUserDto[]
          meta: {
            itemCount: number
            totalItems: number
            itemsPerPage: number
            totalPages: number
            currentPage: number
          }
        }
      })
    | null
  error: Error | null
}

type UsersForAssignEntity = {
  loading: LoadingStateEnum
  data: GetUsersForAssignEntityResponseDto
  error: Error | null
}

type EntitiesForAssignToUser = {
  loading: LoadingStateEnum
  data: ReadBusinessEntitiesForAssignToUserResponseDto
  error: Error | null
}

export const initialState = {
  loading: LoadingStateEnum.IDLE,
  data: {
    items: [],
    meta: {
      itemCount: 0,
      totalItems: 0,
      itemsPerPage: 0,
      totalPages: 0,
      currentPage: 0,
    },
  },
  filters: {},
  error: null,
  createBusinessEntity: {
    loading: LoadingStateEnum.IDLE,
    data: null,
    error: null,
  },
  currentBusinessEntity: {
    id: null,
    loading: LoadingStateEnum.IDLE,
    data: null,
    error: null,
  },
  usersForAssignEntity: {
    loading: LoadingStateEnum.IDLE,
    data: {
      items: [],
      meta: {
        itemCount: 0,
        totalItems: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
  entitiesForAssignToUser: {
    loading: LoadingStateEnum.IDLE,
    data: {
      items: [],
      meta: {
        itemCount: 0,
        totalItems: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
}

export const businessEntities = createReducer<BusinessEntitiesState, ActionTypes>(initialState)
  .handleAction(getBusinessEntitiesRequest, (state, { payload }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...payload,
      page: Object.keys(omit(payload, 'page')).length > 0 ? 1 : payload.page,
    },
    loading: LoadingStateEnum.LOADING,
  }))
  .handleAction(getBusinessEntitiesSuccess, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.SUCCESS,
    data: payload,
  }))
  .handleAction(getBusinessEntitiesFailure, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.ERROR,
    data: initialState.data,
    error: payload,
  }))
  .handleAction(resetBusinessEntitiesFilters, (state) => ({
    ...state,
    filters: initialState.filters,
  }))
  .handleAction(createBusinessEntitiesRequest, (state) => ({
    ...state,
    createBusinessEntity: {
      ...state.createBusinessEntity,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(createBusinessEntitiesSuccess, (state, { payload }) => ({
    ...state,
    createBusinessEntity: {
      ...state.createBusinessEntity,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(createBusinessEntitiesFailure, (state, { payload }) => ({
    ...state,
    createBusinessEntity: {
      ...state.createBusinessEntity,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(resetCreatedBusinessEntity, (state) => ({
    ...state,
    createBusinessEntity: initialState.createBusinessEntity,
  }))
  .handleAction(getBusinessEntityDetailsRequest, (state, { payload }) => ({
    ...state,
    currentBusinessEntity: {
      ...state.currentBusinessEntity,
      id: payload.id,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(
    [
      getBusinessEntityDetailsSuccess,
      updateBusinessEntityDetailsSuccess,
      updateBusinessEntityUsersSuccess,
      deleteBusinessEntityUsersSuccess,
      updateBusinessEntitySettingsSuccess,
    ],
    (state, { payload }) => ({
      ...state,
      currentBusinessEntity: {
        ...state.currentBusinessEntity,
        loading: LoadingStateEnum.SUCCESS,
        data: payload,
      },
    }),
  )
  .handleAction(getBusinessEntityDetailsFailure, (state, { payload }) => ({
    ...state,
    currentBusinessEntity: {
      ...state.currentBusinessEntity,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(getUsersForAssignEntityRequest, (state) => ({
    ...state,
    usersForAssignEntity: {
      ...state.usersForAssignEntity,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getUsersForAssignEntitySuccess, (state, { payload }) => ({
    ...state,
    usersForAssignEntity: {
      ...state.usersForAssignEntity,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getUsersForAssignEntityFailure, (state, { payload }) => ({
    ...state,
    usersForAssignEntity: {
      ...state.usersForAssignEntity,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(getBusinessEntitiesForAssignToUserRequest, (state) => ({
    ...state,
    entitiesForAssignToUser: {
      ...state.entitiesForAssignToUser,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getBusinessEntitiesForAssignToUserSuccess, (state, { payload }) => ({
    ...state,
    entitiesForAssignToUser: {
      ...state.entitiesForAssignToUser,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getBusinessEntitiesForAssignToUserFailure, (state, { payload }) => ({
    ...state,
    entitiesForAssignToUser: {
      ...state.entitiesForAssignToUser,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
